import { supabase } from './supabase';

/**
 * Admin-only billing helpers. All hit Cloudflare Pages Functions that
 * verify the caller's role server-side; client-side role checks here
 * are UX-only.
 */

export interface PriceRow {
  id: string;
  stripe_price_id: string;
  interval: 'monthly' | 'annual';
  tier: string;
  unit_amount_cents: number;
  currency: string;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  archived_at: string | null;
}

export interface PricesResponse {
  active: { monthly: PriceRow | null; annual: PriceRow | null };
  history: PriceRow[];
}

export interface FoundingMember {
  id: string;
  display_name: string | null;
  handle: string | null;
  email: string | null;
  created_at: string;
}

async function authedFetch(path: string, init?: RequestInit) {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess.session?.access_token;
  if (!token) throw new Error('Sign in first.');
  const res = await fetch(path, {
    ...init,
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${token}`,
      ...(init?.headers ?? {}),
    },
  });
  return res;
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = (data as any)?.error ?? `Request failed (${res.status}).`;
    const detail = (data as any)?.detail;
    throw new Error(detail ? `${err}: ${detail}` : err);
  }
  return data as T;
}

// ---------- Prices --------------------------------------------------------

export async function fetchPrices(): Promise<PricesResponse> {
  const res = await authedFetch('/api/admin/billing/prices');
  return jsonOrThrow<PricesResponse>(res);
}

export async function createPrice(args: {
  interval: 'monthly' | 'annual';
  amount_cents: number;
  tier?: string;
}): Promise<{ row: PriceRow }> {
  const res = await authedFetch('/api/admin/billing/prices', {
    method: 'POST',
    body: JSON.stringify({
      interval: args.interval,
      amount_cents: args.amount_cents,
      tier: args.tier ?? 'standard',
    }),
  });
  return jsonOrThrow<{ row: PriceRow }>(res);
}

// ---------- Founding members ---------------------------------------------

export async function fetchFoundingMembers(): Promise<FoundingMember[]> {
  const res = await authedFetch('/api/admin/founding-members');
  const data = await jsonOrThrow<{ members: FoundingMember[] }>(res);
  return data.members;
}

export async function grantFoundingMember(email: string): Promise<{ user_id: string; email: string }> {
  const res = await authedFetch('/api/admin/founding-members', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
  return jsonOrThrow<{ user_id: string; email: string }>(res);
}

export async function revokeFoundingMember(userId: string): Promise<void> {
  const res = await authedFetch(`/api/admin/founding-members/${userId}`, {
    method: 'DELETE',
  });
  await jsonOrThrow<{ ok: true }>(res);
}

// ---------- Helpers -------------------------------------------------------

export function formatCents(cents: number, currency = 'usd'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(cents / 100);
}
